import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import { deletePrize, fetchPrizes, savePrize, setPrizeActive, type Prize } from "@/services/affiliate";

const ICONS = [
  { value: "ri-trophy-fill", label: "Trofeo" },
  { value: "ri-medal-fill", label: "Medalla" },
  { value: "ri-award-fill", label: "Premio" },
  { value: "ri-gift-2-fill", label: "Regalo" },
  { value: "ri-vip-crown-2-fill", label: "Corona" },
  { value: "ri-rocket-2-fill", label: "Cohete" },
];

const inputCls = "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";

export default function AdminAfiliados() {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [modal, setModal] = useState<{ editing: Prize | null } | null>(null);
  const [form, setForm] = useState({ title: "", pointsCost: "1000", icon: ICONS[0].value });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const load = useCallback(async () => setPrizes(await fetchPrizes()), []);

  useEffect(() => {
    let active = true;
    load()
      .catch((err) => active && setError(err instanceof Error ? err.message : "No se pudieron cargar los premios."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const open = (p: Prize | null) => {
    setFormError(null);
    setForm(p ? { title: p.title, pointsCost: String(p.pointsCost), icon: p.icon } : { title: "", pointsCost: "1000", icon: ICONS[0].value });
    setModal({ editing: p });
  };

  const submit = async () => {
    if (!modal) return;
    const cost = Number(form.pointsCost);
    if (!Number.isInteger(cost) || cost < 1) {
      setFormError("Los puntos deben ser un número entero mayor a 0.");
      return;
    }
    setSaving(true);
    try {
      await savePrize({ id: modal.editing?.id, title: form.title.trim(), pointsCost: cost, icon: form.icon });
      setModal(null);
      await load();
      showToast("Premio guardado");
    } catch {
      setFormError("No se pudo guardar. El nombre debe tener entre 3 y 80 caracteres.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Afiliados" description="Premios que tu equipo puede canjear con los puntos del Panel de Afiliado. Publica solo premios que de verdad vas a entregar.">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading font-bold text-foreground-950">Premios</h2>
        <button onClick={() => open(null)} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1"></i>Nuevo premio
        </button>
      </div>

      {loading ? (
        <div className="mt-4 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error ? (
        <div className="mt-4 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error}</div>
      ) : (
        <div className="mt-4 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
          <ul className="divide-y divide-background-200">
            {prizes.map((p) => (
              <li key={p.id} className="p-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-primary-500/15 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-foreground-950 text-sm truncate">{p.title}</div>
                  <div className="text-xs text-foreground-500">{p.pointsCost.toLocaleString("es")} puntos</div>
                </div>
                <button
                  onClick={async () => {
                    await setPrizeActive(p.id, !p.active).catch(() => showToast("No se pudo cambiar el estado."));
                    await load();
                  }}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap ${p.active ? "bg-primary-500/15 text-primary-700" : "bg-background-200 text-foreground-600"}`}
                >
                  {p.active ? "Publicado" : "Oculto"}
                </button>
                <button onClick={() => open(p)} aria-label="Editar premio" className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer">
                  <i className="ri-edit-line"></i>
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar el premio "${p.title}"?`)) return;
                    await deletePrize(p.id).catch(() => showToast("No se pudo eliminar."));
                    await load();
                  }}
                  aria-label="Eliminar premio"
                  className="w-8 h-8 rounded-full text-foreground-500 hover:text-accent-600 hover:bg-background-200 cursor-pointer"
                >
                  <i className="ri-delete-bin-6-line"></i>
                </button>
              </li>
            ))}
            {prizes.length === 0 ? <li className="p-8 text-center text-sm text-foreground-500">Aún no hay premios.</li> : null}
          </ul>
        </div>
      )}

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setModal(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{modal.editing ? "Editar premio" : "Nuevo premio"}</h3>
            <div className="mt-4 space-y-3">
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Nombre del premio" maxLength={80} className={inputCls} />
              <label className="block text-xs font-semibold text-foreground-600">
                Puntos necesarios
                <input value={form.pointsCost} onChange={(e) => setForm((f) => ({ ...f, pointsCost: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
              <select value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} className={`${inputCls} cursor-pointer`}>
                {ICONS.map((i) => (
                  <option key={i.value} value={i.value}>
                    Ícono: {i.label}
                  </option>
                ))}
              </select>
            </div>
            {formError ? <p className="mt-3 text-sm text-accent-700">{formError}</p> : null}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">Cancelar</button>
              <button onClick={submit} disabled={saving || form.title.trim().length < 3} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AdminShell>
  );
}
